export interface GlareResult {
  brightPixelRatio: number;
  largestClusterRatio: number;
  mrzOverlapRatio: number;
  score: number;
  isSevere: boolean;
}

const BRIGHT_THRESHOLD = 240; // near-clipped or fully clipped
const CELL_SIZE = 8; // pixels per grid cell
const GLARE_CELL_RATIO = 0.4; // >40% bright pixels in a cell → glare cell

/**
 * Detects glare by grouping clipped-bright pixels into clusters and checking
 * whether any large cluster overlaps the MRZ text zone.
 */
export function detectGlare(
  lums: Float32Array,
  w: number,
  h: number,
  mrzStartRow: number,
): GlareResult {
  const gridW = Math.floor(w / CELL_SIZE);
  const gridH = Math.floor(h / CELL_SIZE);
  const gridSize = gridW * gridH;
  const cellPixels = CELL_SIZE * CELL_SIZE;

  let totalBright = 0;
  const glareGrid = new Uint8Array(gridSize);

  for (let gy = 0; gy < gridH; gy++) {
    for (let gx = 0; gx < gridW; gx++) {
      let brightCount = 0;
      for (let py = 0; py < CELL_SIZE; py++) {
        for (let px = 0; px < CELL_SIZE; px++) {
          if (lums[(gy * CELL_SIZE + py) * w + (gx * CELL_SIZE + px)] >= BRIGHT_THRESHOLD) {
            brightCount++;
          }
        }
      }
      totalBright += brightCount;
      if (brightCount / cellPixels > GLARE_CELL_RATIO) {
        glareGrid[gy * gridW + gx] = 1;
      }
    }
  }

  const brightPixelRatio = totalBright / (w * h);

  // BFS to find the largest connected glare cluster.
  const visited = new Uint8Array(gridSize);
  let largestCluster = 0;

  const queue: number[] = [];
  const DIRS = [-gridW, gridW, -1, 1] as const;

  for (let start = 0; start < gridSize; start++) {
    if (!glareGrid[start] || visited[start]) continue;
    queue.length = 0;
    queue.push(start);
    visited[start] = 1;
    let qi = 0,
      clusterSize = 0;
    while (qi < queue.length) {
      const cell = queue[qi++];
      clusterSize++;
      const cy = Math.floor(cell / gridW);
      const cx = cell % gridW;
      for (const dir of DIRS) {
        const ni = cell + dir;
        if (ni < 0 || ni >= gridSize || visited[ni] || !glareGrid[ni]) continue;
        // Guard against wrapping at row boundaries.
        if (dir === -1 && cx === 0) continue;
        if (dir === 1 && cx === gridW - 1) continue;
        visited[ni] = 1;
        queue.push(ni);
      }
    }
    if (clusterSize > largestCluster) largestCluster = clusterSize;
  }

  const largestClusterRatio = largestCluster / gridSize;

  // Count glare cells that overlap the MRZ band.
  const mrzStartGridRow = Math.floor(mrzStartRow / CELL_SIZE);
  let mrzGlareCells = 0,
    mrzTotalCells = 0;
  for (let gy = mrzStartGridRow; gy < gridH; gy++) {
    for (let gx = 0; gx < gridW; gx++) {
      mrzTotalCells++;
      if (glareGrid[gy * gridW + gx]) mrzGlareCells++;
    }
  }
  const mrzOverlapRatio = mrzTotalCells > 0 ? mrzGlareCells / mrzTotalCells : 0;

  // Score: penalize large clusters and MRZ overlap.
  const clusterPenalty = Math.min(1, largestClusterRatio / 0.15);
  const mrzPenalty = Math.min(1, mrzOverlapRatio / 0.2);
  const score = Math.max(0, 1 - clusterPenalty * 0.6 - mrzPenalty * 0.4);

  const isSevere = mrzOverlapRatio > 0.3 || largestClusterRatio > 0.25;

  return { brightPixelRatio, largestClusterRatio, mrzOverlapRatio, score, isSevere };
}
